import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { SEASONS, DIVISIONS, AWARDS, GENDERS } from '../data/standoutsConstants';
import StandoutAthleteCard from '../components/StandoutAthleteCard';
import '../styles/OrgStandouts.css';

const formatName = (n) => {
  if (!n) return '';
  const p = n.trim().split(' ');
  return p.length < 2 ? n : `${p[0]} ${p[p.length - 1].charAt(0)}.`;
};

const FilterDropdown = ({ iconSrc, value, options, onChange, isActive, searchable }) => {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState('');
  const ref = React.useRef(null);

  React.useEffect(() => {
    const close = (e) => {
      if (ref.current && !ref.current.contains(e.target)) { setOpen(false); setQuery(''); }
    };
    document.addEventListener('mousedown', close);
    document.addEventListener('touchstart', close, { passive: true });
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('touchstart', close);
    };
  }, []);

  const selected = options.find((o) => o.value === value);
  const visible = (searchable && query
    ? options.filter((o) => o.label.toLowerCase().includes(query.toLowerCase()))
    : options).filter((o) => !o.hidden);

  return (
    <div className="sland-filter-wrap" ref={ref}>
      <button
        type="button"
        className={`sland-filter-btn sland-filter-has-icon${isActive ? ' active' : ''}${open ? ' open' : ''}`}
        onClick={() => { setOpen((prev) => !prev); setQuery(''); }}
      >
        {iconSrc && <img src={iconSrc} alt="" className="sland-filter-icon" />}
        <span className="sland-filter-label">{selected ? selected.label : options[0]?.label}</span>
        <img src="/assets/images/filter-arrow-down.png" alt="" className="sland-filter-arrow" />
      </button>
      {open && (
        <div className="sland-filter-dropdown">
          {searchable && (
            <div style={{ padding: '6px 8px', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
              <input
                autoFocus
                style={{ width: '100%', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(216,161,52,0.3)', borderRadius: '6px', padding: '6px 10px', color: '#fff', fontSize: '13px', outline: 'none', boxSizing: 'border-box' }}
                placeholder="Search..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onMouseDown={(e) => e.stopPropagation()}
              />
            </div>
          )}
          {visible.map((opt) => (
            <div
              key={opt.value}
              className={`sland-filter-option${opt.value === value ? ' selected' : ''}`}
              onMouseDown={(e) => { e.preventDefault(); onChange(opt.value); setOpen(false); setQuery(''); }}
              onTouchEnd={(e) => { e.preventDefault(); onChange(opt.value); setOpen(false); setQuery(''); }}
            >
              {opt.label}
            </div>
          ))}
          {visible.length === 0 && (
            <div style={{ padding: '10px 14px', fontSize: '13px', color: 'rgba(255,255,255,0.4)' }}>No results</div>
          )}
        </div>
      )}
    </div>
  );
};

const OrgStandouts = () => {
  const { orgSlug } = useParams();
  const navigate = useNavigate();
  const { getApprovedStandouts, getOrganizationBySlug, getApprovedOrganizations } = useAuth();

  const [allOrgStandouts, setAllOrgStandouts] = useState([]);
  const [orgFromDb, setOrgFromDb] = useState(null);
  const [allApprovedOrgs, setAllApprovedOrgs] = useState([]);
  const [loading, setLoading] = useState(true);

  const [selectedWeek, setSelectedWeek] = useState('all');
  const [selectedSeason, setSelectedSeason] = useState('all');
  const [selectedGender, setSelectedGender] = useState('all');
  const [selectedAward, setSelectedAward] = useState('all');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const [standoutsResult, orgResult] = await Promise.all([
        getApprovedStandouts({ orgSlug }),
        getOrganizationBySlug(orgSlug),
      ]);
      if (cancelled) return;
      if (standoutsResult.success) setAllOrgStandouts(standoutsResult.data || []);
      if (orgResult.success && orgResult.data) setOrgFromDb(orgResult.data);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [orgSlug, getApprovedStandouts, getOrganizationBySlug]);

  useEffect(() => {
    getApprovedOrganizations().then((result) => {
      if (result.success) setAllApprovedOrgs(result.data || []);
    });
  }, [getApprovedOrganizations]);

  // Prefer standout-derived info (has org_location string), fall back to DB record
  const org = (() => {
    if (allOrgStandouts.length > 0) {
      return {
        name: allOrgStandouts[0].org_name,
        sport: allOrgStandouts[0].sport,
        location: allOrgStandouts[0].org_location || '',
        slug: allOrgStandouts[0].org_slug,
      };
    }
    if (orgFromDb) {
      const parts = [orgFromDb.city, orgFromDb.state].filter(Boolean);
      return {
        name: orgFromDb.name,
        sport: orgFromDb.sport || '',
        location: parts.join(', '),
        slug: orgFromDb.slug,
      };
    }
    return null;
  })();

  const weekFiltered = (() => {
    if (selectedWeek === 'all') return allOrgStandouts;

    if (selectedWeek === 'current') {
      const now = new Date();
      const day = now.getDay();
      const lastMonday = new Date(now);
      const daysBack = day === 0 ? 6 : day - 1;
      lastMonday.setDate(now.getDate() - daysBack);
      lastMonday.setHours(5, 0, 0, 0);
      if (now < lastMonday) lastMonday.setDate(lastMonday.getDate() - 7);
      return allOrgStandouts.filter((s) => new Date(s.published_at || s.created_at) >= lastMonday);
    }

    if (selectedWeek === 'last_week') {
      const now = new Date();
      const day = now.getDay();
      const startOfThisWeek = new Date(now);
      startOfThisWeek.setDate(now.getDate() - (day === 0 ? 6 : day - 1));
      startOfThisWeek.setHours(0, 0, 0, 0);
      const startOfLastWeek = new Date(startOfThisWeek);
      startOfLastWeek.setDate(startOfThisWeek.getDate() - 7);
      return allOrgStandouts.filter((s) => {
        const d = new Date(s.published_at || s.created_at);
        return d >= startOfLastWeek && d < startOfThisWeek;
      });
    }

    if (selectedWeek === 'this_month') {
      const cutoff = new Date();
      cutoff.setDate(cutoff.getDate() - 30);
      return allOrgStandouts.filter((s) => new Date(s.published_at || s.created_at) >= cutoff);
    }

    if (selectedWeek === 'this_year') {
      const year = new Date().getFullYear();
      return allOrgStandouts.filter((s) => new Date(s.published_at || s.created_at).getFullYear() === year);
    }

    if (selectedWeek === 'this') {
      const year = new Date().getFullYear();
      if (selectedSeason === 'all') return allOrgStandouts.filter((s) => s.season?.endsWith(` ${year}`));
      return allOrgStandouts.filter((s) => s.season === `${selectedSeason} ${year}`);
    }

    return allOrgStandouts;
  })();

  const filtered = weekFiltered.filter((s) => {
    return (
      (selectedGender === 'all' || s.gender === selectedGender) &&
      (selectedAward === 'all' || s.award === selectedAward)
    );
  });

  if (loading) {
    return (
      <div className="os-page">
        <div className="os-topbar">
          <button className="os-back-link" onClick={() => navigate('/standouts')}>
            <span className="os-back-arrow">←</span>
            <span className="os-back-text">Back to Standouts</span>
          </button>
          <img src="/assets/images/logo.png" alt="LegacyStat" className="os-logo" />
        </div>
        <div className="os-inner">
          {/* Title skeleton */}
          <div className="os-title-block">
            <div className="os-skel os-skel-title" />
            <div className="os-skel os-skel-org-name" />
            <div className="os-skel os-skel-location" />
            <div className="os-skel os-skel-coaches" />
            <div className="os-skel os-skel-tagline" />
            <div className="os-skel os-skel-link" />
          </div>
          {/* Filters skeleton */}
          <div className="sland-filters" style={{ marginBottom: '24px' }}>
            {[1,2,3,4].map((i) => <div key={i} className="os-skel os-skel-filter" />)}
          </div>
          {/* Cards skeleton */}
          <div className="os-grid">
            {[1,2,3,4].map((i) => <div key={i} className="os-skel os-skel-card" />)}
          </div>
        </div>
      </div>
    );
  }

  if (!org) {
    return (
      <div className="os-page">
        <div className="os-inner">
          <p style={{ color: 'rgba(255,255,255,0.4)', padding: '80px 0', textAlign: 'center' }}>
            Organization not found.
          </p>
          <button className="os-back-link" onClick={() => navigate('/standouts')}>
            ← Back to Standouts
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="os-page">

      {/* ── Top Bar ── */}
      <div className="os-topbar">
        <button className="os-back-link" onClick={() => navigate('/standouts')}>
          <span className="os-back-arrow">←</span>
          <span className="os-back-text">Back to Standouts</span>
        </button>
        <img src="/assets/images/logo.png" alt="LegacyStat" className="os-logo" />
      </div>

      <div className="os-inner">

        {/* ── Title Block ── */}
        <div className="os-title-block">
          <h1 className="os-standouts-title">STANDOUTS</h1>
          <h2 className="os-main-title">{(org.name || '').toUpperCase()}</h2>
          <div className="os-meta-location">
            <img src="/assets/images/location-Featured.png" alt="" className="os-meta-icon" />
            {org.location}
          </div>
          <div className="os-coaches-row">
            <span className="os-coaches-dash" />
            <span className="os-coaches-text">COACH SELECTED</span>
            <span className="os-coaches-dash" />
          </div>
          <p className="os-tagline">Weekly recognition for standout performances.</p>
          <p style={{ margin: '0 0 4px' }}>
            <button className="os-org-link" onClick={() => navigate('/standouts/submit')}>
              → Add your Standouts
            </button>
          </p>
        </div>


        {/* ── Filters ── */}
        <div className="sland-filters" style={{ marginBottom: '24px' }}>
          <FilterDropdown
            iconSrc="/assets/images/filter-org.png"
            value={orgSlug}
            options={[
              { value: orgSlug, label: org?.name || 'Organizations', hidden: true },
              ...allApprovedOrgs.map((o) => ({ value: o.slug || o.id, label: o.name })),
            ]}
            onChange={(v) => { if (v !== orgSlug) navigate(`/standouts/org/${v}`); }}
            isActive={true}
            searchable
          />
          <FilterDropdown
            iconSrc="/assets/images/filter-recent.png"
            value={selectedWeek}
            options={[
              { value: 'all', label: 'All Weeks', hidden: true },
              { value: 'current', label: 'This Week' },
              { value: 'last_week', label: 'Last Week' },
              { value: 'this_month', label: 'This Month' },
              { value: 'this', label: 'This Season' },
              { value: 'this_year', label: 'This Year' },
            ]}
            onChange={(v) => { setSelectedWeek(v); if (v !== 'this') setSelectedSeason('all'); }}
            isActive={selectedWeek !== 'all'}
          />
          {selectedWeek === 'this' && (
            <FilterDropdown
              iconSrc="/assets/images/filter-recent.png"
              value={selectedSeason}
              options={[
                { value: 'all', label: 'All Seasons' },
                { value: 'Spring', label: 'Spring' },
                { value: 'Summer', label: 'Summer' },
                { value: 'Fall', label: 'Fall' },
                { value: 'Winter', label: 'Winter' },
              ]}
              onChange={setSelectedSeason}
              isActive={selectedSeason !== 'all'}
            />
          )}
          <FilterDropdown
            iconSrc="/assets/images/filter-location.png"
            value={selectedGender}
            options={[
              { value: 'all', label: 'All Divisions' },
              ...DIVISIONS.map((d) => ({ value: d, label: d })),
            ]}
            onChange={setSelectedGender}
            isActive={selectedGender !== 'all'}
          />
          <FilterDropdown
            iconSrc="/assets/images/filter-recent.png"
            value={selectedAward}
            options={[
              { value: 'all', label: 'All Awards' },
              ...AWARDS.map((a) => ({ value: a, label: a })),
            ]}
            onChange={setSelectedAward}
            isActive={selectedAward !== 'all'}
          />
        </div>

        {/* ── View All Weeks ── */}

        {/* ── Grid ── */}
        {filtered.length === 0 ? (
          <div className="os-empty">No standouts match your filters.</div>
        ) : (
          <div className="os-grid">
            {filtered.map((s) => (
              <StandoutAthleteCard
                key={s.id}
                image={s.photo_url}
                name={formatName(s.athlete_name)}
                sport={org.name}
                sportType={s.sport}
                award={s.award}
                week={s.week_label}
                level={s.level}
                showWeekLabel={true}
                onBadgeClick={() => navigate(`/standouts/athlete/${s.id}`)}
              />
            ))}
          </div>
        )}


        {/* ── Org Banner ── */}
        <div className="os-org-banner">
          <div className="os-banner-left">
            <div className="os-banner-icon">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" stroke="#D8A134" strokeWidth="1.8" strokeLinecap="round"/>
                <circle cx="9" cy="7" r="4" stroke="#D8A134" strokeWidth="1.8" strokeLinecap="round"/>
                <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" stroke="#D8A134" strokeWidth="1.8" strokeLinecap="round"/>
              </svg>
            </div>
            <div>
              <p className="os-banner-title">Get your organization on LegacyStat</p>
              <p className="os-banner-sub">Nominate players. Publish standouts weekly. Build your organization's presence.</p>
            </div>
          </div>
          <button className="os-banner-btn" onClick={() => navigate('/standouts/submit')}>
            Add Your Organizations Standouts
          </button>
        </div>

      </div>

      {/* ── Footer ── */}
      <footer className="os-footer">
        <div className="os-footer-left">
          <img src="/assets/images/logo.png" alt="LegacyStat" className="os-footer-logo" />
        </div>
        <p className="os-footer-copy">© 2026 LegacyStat. All rights reserved.</p>
        <div className="os-footer-links">
          <a href="/privacy" className="os-footer-link">Privacy Policy</a>
          <span className="os-footer-sep">|</span>
          <a href="/terms" className="os-footer-link">Terms of Service</a>
        </div>
      </footer>

    </div>
  );
};

export default OrgStandouts;
